import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  NgZone
} from '@angular/core';
import * as THREE from 'three';

const MAX_COLORS = 8;

const frag = `
#define MAX_COLORS ${MAX_COLORS}
uniform vec2 uCanvas;
uniform float uTime;
uniform float uSpeed;
uniform vec2 uRot;
uniform int uColorCount;
uniform vec3 uColors[MAX_COLORS];
uniform int uTransparent;
uniform float uScale;
uniform float uFrequency;
uniform float uWarpStrength;
uniform vec2 uPointer; // in NDC [-1,1]
uniform float uMouseInfluence;
uniform float uParallax;
uniform float uNoise;
varying vec2 vUv;

void main() {
  float t = uTime * uSpeed;
  vec2 p = vUv * 2.0 - 1.0;
  p += uPointer * uParallax * 0.1;
  vec2 rp = vec2(p.x * uRot.x - p.y * uRot.y, p.x * uRot.y + p.y * uRot.x);
  vec2 q = vec2(rp.x * (uCanvas.x / uCanvas.y), rp.y);
  q /= max(uScale, 0.0001);
  q /= 0.5 + 0.2 * dot(q, q);
  q += 0.2 * cos(t) - 7.56;
  vec2 toward = (uPointer - rp);
  q += toward * uMouseInfluence * 0.2;

    vec3 col = vec3(0.0);
    float a = 1.0;

    if (uColorCount > 0) {
      vec2 s = q;
      vec3 sumCol = vec3(0.0);
      float cover = 0.0;
      for (int i = 0; i < MAX_COLORS; ++i) {
            if (i >= uColorCount) break;
            s -= 0.01;
            // Use uWarpStrength to directly scale the inner phase distortion
            vec2 r = sin(1.5 * (s.yx * uFrequency) + (2.0 * uWarpStrength) * cos(s * uFrequency));
            float m = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(i)) / 4.0);
            float w = 1.0 - exp(-6.0 / exp(6.0 * m));
            sumCol += uColors[i] * w;
            cover = max(cover, w);
      }
      col = clamp(sumCol, 0.0, 1.0);
      a = uTransparent > 0 ? cover : 1.0;
    } else {
        vec2 s = q;
        for (int k = 0; k < 3; ++k) {
            s -= 0.01;
            vec2 r = sin(1.5 * (s.yx * uFrequency) + (2.0 * uWarpStrength) * cos(s * uFrequency));
            float m = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(k)) / 4.0);
            col[k] = 1.0 - exp(-6.0 / exp(6.0 * m));
        }
        a = uTransparent > 0 ? max(max(col.r, col.g), col.b) : 1.0;
    }

    if (uNoise > 0.0001) {
      float n = fract(sin(dot(gl_FragCoord.xy + vec2(uTime), vec2(12.9898, 78.233))) * 43758.5453123);
      col += (n - 0.5) * uNoise;
      col = clamp(col, 0.0, 1.0);
    }

    vec3 rgb = (uTransparent > 0) ? col * a : col;
    gl_FragColor = vec4(rgb, a);
}
`;

const vert = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

@Component({
  selector: 'nc-color-bends-background',
  templateUrl: './color-bends-background.component.html',
  styleUrls: ['./color-bends-background.component.less']
})
export class ColorBendsBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 容器背景颜色 */
  @Input() ncBgColor: string = 'black';
  /** 旋转角度（deg -180 - 180） */
  @Input() ncRotation: number = 0;
  /** 动画速度（0-1）*/
  @Input() ncSpeed: number = 0.2;
  /** 用于混合弯曲部分的颜色（十六进制字符串数组，最大支持8个颜色） */
  @Input() ncColors: string[] = [];
  /** 是否开启透明背景 */
  private _transparent: boolean = true;
  @Input()
  set ncTransparent(val: boolean | string) {
    this._transparent = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncTransparent(): boolean {
    return this._transparent;
  }
  /** 自动旋转速度（deg/s -5-5） */
  @Input() ncAutoRotate: number = 0;
  /** 缩放比例（0.2-5） */
  @Input() ncScale: number = 1;
  /** 频率（0-5） */
  @Input() ncFrequency: number = 1;
  /** 扭曲强度（0-1） */
  @Input() ncWarpStrength: number = 1;
  /** 鼠标影响力的强度（0-2） */
  @Input() ncMouseInfluence: number = 1;
  /** 视差效果强度，视差效果是指指针移动内容时产生的效果（0-2） */
  @Input() ncParallax: number = 0.5;
  /** 噪点强度（0-1） */
  @Input() ncNoise: number = 0.1;

  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private renderer!: THREE.WebGLRenderer;
  private material!: THREE.ShaderMaterial;
  private clock!: THREE.Clock;
  private geometry!: THREE.PlaneGeometry;

  private pointerTarget = new THREE.Vector2(0, 0);
  private pointerCurrent = new THREE.Vector2(0, 0);
  private pointerSmooth = 8;

  private resizeObserver!: ResizeObserver;
  private intersectionObserver!: IntersectionObserver;
  private isVisible = true;
  private rafId: number | null = null;
  private resizeRafId: number | null = null;

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initScene();
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.material) return;
    this.updateUniforms();
  }

  private initScene(): void {
    const container = this.containerRef.nativeElement;

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this.geometry = new THREE.PlaneGeometry(2, 2);
    const uColorsArray = Array.from({ length: MAX_COLORS }, () => new THREE.Vector3(0, 0, 0));

    this.material = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: {
        uCanvas: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 },
        uSpeed: { value: Number(this.ncSpeed) },
        uRot: { value: new THREE.Vector2(1, 0) },
        uColorCount: { value: 0 },
        uColors: { value: uColorsArray },
        uTransparent: { value: this.ncTransparent ? 1 : 0 },
        uScale: { value: Number(this.ncScale) },
        uFrequency: { value: Number(this.ncFrequency) },
        uWarpStrength: { value: Number(this.ncWarpStrength) },
        uPointer: { value: new THREE.Vector2(0, 0) },
        uMouseInfluence: { value: Number(this.ncMouseInfluence) },
        uParallax: { value: Number(this.ncParallax) },
        uNoise: { value: Number(this.ncNoise) }
      },
      premultipliedAlpha: true,
      transparent: true
    });

    const mesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(mesh);

    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      alpha: true
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0x000000, this.ncTransparent ? 0 : 1);
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.display = 'block';

    container.appendChild(this.renderer.domElement);

    this.clock = new THREE.Clock();

    this.updateUniforms();
    this.setupResizeObserver(container);
    this.setupIntersectionObserver(container);

    this.loop();
  }

  private updateUniforms(): void {
    if (!this.material) return;

    this.material.uniforms['uSpeed'].value = Number(this.ncSpeed);
    this.material.uniforms['uScale'].value = Number(this.ncScale);
    this.material.uniforms['uFrequency'].value = Number(this.ncFrequency);
    this.material.uniforms['uWarpStrength'].value = Number(this.ncWarpStrength);
    this.material.uniforms['uMouseInfluence'].value = Number(this.ncMouseInfluence);
    this.material.uniforms['uParallax'].value = Number(this.ncParallax);
    this.material.uniforms['uNoise'].value = Number(this.ncNoise);
    this.material.uniforms['uTransparent'].value = this.ncTransparent ? 1 : 0;

    if (this.renderer) {
      this.renderer.setClearColor(0x000000, this.ncTransparent ? 0 : 1);
    }

    const toVec3 = (hex: string) => {
      const h = hex.replace('#', '').trim();
      const v = h.length === 3
        ? [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)]
        : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
      return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
    };

    const arr = (this.ncColors || []).filter(Boolean).slice(0, MAX_COLORS).map(toVec3);
    for (let i = 0; i < MAX_COLORS; i++) {
      const vec = this.material.uniforms['uColors'].value[i];
      if (i < arr.length) vec.copy(arr[i]);
      else vec.set(0, 0, 0);
    }
    this.material.uniforms['uColorCount'].value = arr.length;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(e: PointerEvent): void {
    if (!this.containerRef?.nativeElement) return;
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / (rect.width || 1)) * 2 - 1;
    const y = -(((e.clientY - rect.top) / (rect.height || 1)) * 2 - 1);
    this.pointerTarget.set(x, y);
  }

  private resize(container: HTMLElement): void {
    if (!this.renderer || !this.material) return;
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.material.uniforms['uCanvas'].value.set(w, h);
  }

  private setupResizeObserver(container: HTMLElement): void {
    this.resize(container);
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.renderer) return;
      if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
      this.ngZone.runOutsideAngular(() => {
        this.resizeRafId = requestAnimationFrame(() => {
          if (!this.renderer) return;
          this.resize(container);
          this.resizeRafId = null;
        });
      });
    });
    this.resizeObserver.observe(container);
  }

  private setupIntersectionObserver(container: HTMLElement): void {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      this.isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
    }, { threshold: [0, 0.01, 0.1] });
    this.intersectionObserver.observe(container);
  }

  private loop = (): void => {
    this.rafId = requestAnimationFrame(this.loop);

    // 只在可见窗口时渲染
    if (!this.isVisible || document.hidden) {
      if (this.clock) this.clock.getDelta(); // 防止重新唤醒时时间突变
      return;
    }
    if (!this.renderer || !this.scene || !this.camera) return;

    const dt = this.clock.getDelta();
    const elapsed = this.clock.elapsedTime;
    this.material.uniforms['uTime'].value = elapsed;

    const deg = (Number(this.ncRotation) % 360) + Number(this.ncAutoRotate) * elapsed;
    const rad = (deg * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    this.material.uniforms['uRot'].value.set(c, s);

    const amt = Math.min(1, dt * this.pointerSmooth);
    this.pointerCurrent.lerp(this.pointerTarget, amt);
    this.material.uniforms['uPointer'].value.copy(this.pointerCurrent);

    this.renderer.render(this.scene, this.camera);
  };

  private cleanup(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    try {
      this.resizeObserver?.disconnect();
    } catch (e) { void 0; }
    try {
      this.intersectionObserver?.disconnect();
    } catch (e) { void 0; }

    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
    if (this.renderer) {
      const dom = this.renderer.domElement;
      if (dom && dom.parentNode) {
        dom.parentNode.removeChild(dom);
      }
      this.renderer.dispose();
    }
  }
}
